import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { luxonFormats, luxonViews } from "../../../EConsent/Builder/blockTypes";
import { useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import {
  setProviderResponse,
  updateBlockEditable,
} from "../../../../Redux/reducers/providerConsentSlice";
import { errorToastMessage, toastMessage } from "../../../../utils/toast";
import { DateTime } from "luxon";

const ProviderSignature = ({
  isName,
  isAddress,
  isDate,
  response,
  changeHandler,
  clear,
  sigCanvas,
  format,
  submitSignature,
}: any) => {
  return (
    <>
      {isName && (
        <Stack gap={1}>
          <Stack gap={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              First Name
            </Typography>
            <TextField
              name="firstName"
              sx={{
                width: {
                  md: 350,
                  xs: "100%",
                },
                maxWidth: "100%",
              }}
              placeholder="First Name"
              value={response?.firstName || ""}
              onChange={(e) => changeHandler(e.target.value, "firstName")}
            />
          </Stack>
          <Stack gap={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              Last Name
            </Typography>
            <TextField
              name="lastName"
              sx={{
                width: {
                  md: 350,
                  xs: "100%",
                },
                maxWidth: "100%",
              }}
              placeholder="Last Name"
              value={response?.lastName || ""}
              onChange={(e) => changeHandler(e.target.value, "lastName")}
            />
          </Stack>
        </Stack>
      )}
      {isAddress && (
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight="medium">
            Address
          </Typography>
          <TextField
            multiline
            rows={4}
            name="address"
            sx={{
              width: {
                md: 350,
                xs: "100%",
              },
              maxWidth: "100%",
            }}
            placeholder="Address"
            value={response?.address || ""}
            onChange={(e) => changeHandler(e.target.value, "address")}
          />
        </Stack>
      )}
      {isDate && (
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight="medium">
            Date
          </Typography>

          <DateTimePicker
            format={luxonFormats?.[format]}
            views={luxonViews?.[format]}
            value={
              response?.date
                ? DateTime.fromFormat(response?.date, luxonFormats?.[format])
                : null
            }
            onChange={(newDate) =>
              changeHandler(newDate?.toFormat(luxonFormats?.[format]), "date")
            }
            slotProps={{
              textField: {
                sx: {
                  width: {
                    md: 350,
                    xs: "100%",
                  },
                  maxWidth: "100%",
                },
                InputProps: {
                  readOnly: true,
                  placeholder: "Select date",
                },
              },
              field: {
                readOnly: true,
              },
            }}
          />
        </Stack>
      )}
      <Stack gap={1}>
        <Typography variant="subtitle1" fontWeight="medium">
          Signature
        </Typography>
        {response?.signature ? (
          <img
            src={response?.signature}
            alt="signature"
            style={{
              borderRadius: "6px",
              width: "300px",
            }}
          />
        ) : (
          <Box
            sx={{
              width: {
                md: "80%",
                xs: "100%",
              },
            }}
          >
            <SignatureCanvas
              ref={(ref) => {
                sigCanvas.current = ref;
              }}
              canvasProps={{ className: "canvas-element" }}
            />
            <Box my={2}>
              <Button variant="contained" onClick={submitSignature}>
                Save Signature
              </Button>
              <Button sx={{ ml: "10px" }} variant="outlined" onClick={clear}>
                Clear
              </Button>
            </Box>
          </Box>
        )}
      </Stack>
    </>
  );
};

const PatientSignature = ({ isName, isAddress, isDate, response }: any) => {
  return (
    <>
      {isName && (
        <Stack gap={1}>
          <Stack gap={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              First Name
            </Typography>
            <Typography variant="body1">
              {response?.firstName || "-"}
            </Typography>
          </Stack>
          <Stack gap={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              Last Name
            </Typography>
            <Typography variant="body1">{response?.lastName || "-"}</Typography>
          </Stack>
        </Stack>
      )}
      {isAddress && (
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight="medium">
            Address
          </Typography>
          <Typography variant="body1">{response?.address || "-"}</Typography>
        </Stack>
      )}
      {isDate && (
        <Stack gap={1}>
          <Typography variant="subtitle1" fontWeight="medium">
            Date
          </Typography>
          <Typography variant="body1">{response?.date || "-"}</Typography>
        </Stack>
      )}
      <Stack gap={1}>
        <Typography variant="subtitle1" fontWeight="medium">
          Signature
        </Typography>
        {response?.signature ? (
          <img
            src={response?.signature}
            alt="signature"
            style={{
              borderRadius: "6px",
              width: "300px",
            }}
          />
        ) : (
          <Typography variant="body1">{response?.signature || "-"}</Typography>
        )}
      </Stack>
    </>
  );
};

type Props = {
  currentBlock: any;
};

const SignatureField = ({ currentBlock }: Props) => {
  const dispatch = useAppDispatch();

  const readonly = useAppSelector((state) => state.providerConsent.readonly);
  const label = currentBlock?.label;
  const isName = currentBlock?.config?.name;
  const isAddress = currentBlock?.config?.address;
  const isDate = currentBlock?.config?.date;
  const format = currentBlock?.config?.format;
  const response = currentBlock?.response || {};
  const sigCanvas = useRef<any>();

  const clear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const submitSignature = async () => {
    try {
      if (!currentBlock?.response?.firstName && isName) {
        toastMessage("warning", "First Name Cannot be empty");
        return;
      } else if (!currentBlock?.response?.lastName && isName) {
        toastMessage("warning", "Last Name Cannot be empty");
        return;
      } else if (!currentBlock?.response?.address && isAddress) {
        toastMessage("warning", "Address Cannot be empty");
        return;
      } else if (!currentBlock?.response?.date && isDate) {
        toastMessage("warning", "Date Cannot be empty");
        return;
      }

      if (sigCanvas.current) {
        if (sigCanvas.current.isEmpty()) {
          toastMessage("warning", "Signature Cannot be empty");
          return;
        }
      } else {
        toastMessage("warning", "Unable to fetch Signature");
        return;
      }

      const image = sigCanvas.current.toDataURL();

      const updatedResponse: any = {
        signature: image,
      };

      if (isName) {
        updatedResponse.firstName = response?.firstName;
        updatedResponse.lastName = response?.lastName;
      }
      if (isAddress) {
        updatedResponse.address = response?.address;
      }
      if (isDate) {
        updatedResponse.date = response?.date;
      }

      dispatch(
        setProviderResponse({
          blockId: currentBlock?.id,
          response: {
            ...updatedResponse,
          },
        })
      );
      dispatch(
        updateBlockEditable({
          blockId: currentBlock?.id,
          editable: false,
        })
      );
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
    }
  };

  const changeHandler = (value: any, field: string) => {
    const updatedResponse = {
      ...response,
      [field]: value,
    };

    dispatch(
      setProviderResponse({
        blockId: currentBlock?.id,
        response: updatedResponse,
      })
    );
  };

  if (currentBlock?.config?.provider && !readonly) {
    return null;
  }

  return (
    <Stack gap={1}>
      {label && (
        <Typography
          variant="h2"
          sx={{
            fontWeight: "regular",
            color: "text.primary",
          }}
        >
          {label}
        </Typography>
      )}
      {currentBlock.editable ? (
        <ProviderSignature
          isName={isName}
          isAddress={isAddress}
          isDate={isDate}
          format={format}
          response={response}
          sigCanvas={sigCanvas}
          changeHandler={changeHandler}
          clear={clear}
          submitSignature={submitSignature}
        />
      ) : (
        <PatientSignature
          isName={isName}
          isAddress={isAddress}
          isDate={isDate}
          response={response}
        />
      )}
    </Stack>
  );
};
export default SignatureField;
